<template>
  <div
    class="product-grid grid"
    :class="{
      [$class('loading')]: state === 'initial-load' || campaignLoading,
      [$class('collapsed')]: partial,
      [`product-grid--${template}`]: template,
    }"
  >
    <div v-if="!partial" class="col xs-span l12 product-grid__filters">
      <div
        class="product-grid__sort-by text-mobile-paragraph-s-regular text-desktop-paragraph-s-regular"
      >
        <icon-up-down />

        <div class="product-grid__sort">
          <label
            class="text-mobile-paragraph-s-regular text-desktop-paragraph-s-regular"
            for="sort-options"
            v-text="$string('collection.sort_by')"
          />

          <select
            id="sort-options"
            v-model="currentSort"
            class="product-grid__sort-select text-mobile-paragraph-s-regular-link text-desktop-paragraph-s-regular-link"
            @change="handleSort"
          >
            <option
              v-for="option of sortOptions"
              :key="option.value.sort"
              :selected="currentSort === option.value"
              :value="option.value"
              v-text="option.title"
            />
          </select>
        </div>
      </div>

      <p
        class="product-grid__product-count text-mobile-paragraph-s-regular text-desktop-paragraph-s-regular"
        :class="{
          [$class('loading')]:
            ['loading', 'initial-load'].includes(state) || campaignLoading,
        }"
        v-text="productCountString"
      />
    </div>

    <!-- Empty loading state -->
    <template
      v-if="
        (contentList && !contentList.length && state === 'loading') ||
        campaignLoading
      "
    >
      <div
        v-for="index of productsPerPage"
        :key="`loading-${index}`"
        class="col xs3 m2 l3"
      >
        <product-card :product="false" />
      </div>
    </template>

    <!-- Empty collection -->
    <template
      v-else-if="(state === 'ready' && !contentList) || !contentList.length"
    >
      <div class="col xs-span">
        <p
          class="text-mobile-paragraph-s-regular text-desktop-paragraph-s-regular text-center"
          v-text="$string('collection.no_products')"
        />
      </div>
    </template>

    <!-- Product grid -->
    <template v-else>
      <div
        v-for="(content, index) of gridProducts"
        :key="`${content.handle}-${index}`"
        class="col"
        :class="
          content.featuredBanner && !hideBanner ? 'xs-span m4 l6' : 'xs3 m2 l3'
        "
      >
        <product-card
          v-if="content?.cardType === 'product'"
          :is-campaign="campaignProducts.length > 0"
          :discount-preview="discountPreview"
          :display-banner="!hideBanner"
          :index="index"
          :is-variant="content.isVariant"
          :lazyload="index > 4"
          :location="template"
          :product="content"
          :search-query="template === 'search' ? query : ''"
          :variant="content.selectedOrFirstAvailableVariant"
        />

        <disturber-card
          v-if="content?.cardType === 'disturber'"
          :card-data="content"
        />
      </div>
    </template>

    <div
      v-if="page.hasNextPage || (nextCampaignPage && handleBtnClick)"
      class="col xs-span l12"
      :class="[
        template === 'campaign'
          ? 'campaign-product-list__load-more'
          : 'product-grid__load-more',
      ]"
    >
      <btn
        ref="loadButton"
        class-label="
          text-mobile-paragraph-s-regular
          text-desktop-paragraph-s-regular
        "
        :label="$string('collection.load_more')"
        modifiers="fill secondary center"
        :show-state-icon="state === 'loading' || parentState === 'loading'"
        @click="
          template === 'campaign' && handleBtnClick
            ? handleBtnClick(true)
            : queryCollection(true)
        "
        @vue:mounted="loadButtonMounted"
      >
        <template #stateIcon>
          <icon-loading />
        </template>
      </btn>
    </div>
  </div>
</template>

<script>
/**
 * Vue: Product grid (product-grid)
 * -----------------------------------------------------------------------------
 * Product grid page component.
 *
 * @param {Boolean|Array} [campaignProducts] - Array of pre-formatted products
 * to  display, overrides IDs and disables loading products, for discount
 * campaign use only.
 * @param {Number} collectionProductCount - Product count to display.
 * @param {String} defaultSortBy - Default sort by option.
 * @param {Boolean} [discountPreview] - Show preview of promoted campaign in
 * product card badges and prices.
 * @param {Array} disturber - Grid disturbers to display.
 * @param {String} handle - Collection or metaobject handle.
 * @param {Boolean} partial - FOMO grid on campaign page.
 * @param {Number} productsPerPage - Products to display per page.
 * @param {String} template - Shopify template name.
 * @param {Boolean} hideSoldout - Theme setting to hide soldout products
 * @param {Boolean} nextCampaignPage - The page.hasNextPage equivalent of
 * campaign-product-list
 * @param {Function} handleBtnClick - Custom load more button click handler
 * @param {String} parentState - Loading state of parent component
 * @param {Boolean} infiniteScroll - Theme setting to use infinite scrolling
 *
 * @emits state - Current state.
 *
 */
import { mapActions, mapState } from 'pinia';
import usePredictiveSearchStore from '~stores/predictive-search';
import useIndexStore from '~stores/index';
import useDiscountCampaignsStore from '~stores/discount-campaigns';
import { computeDiscountLogic } from '~/helpers/vue';
import useCartStore from '~stores/cart';

import Btn from '~global/btn/btn';
import DisturberCard from '~async/disturber-card/disturber-card';
import ProductCard from '~global/product-card/product-card';

import IconLoading from '~icons/general/loading.svg';
import IconUpDown from '~icons/directional-navigation/arrow/up-down.svg';

import collectionQuery from '~graphql/collection.gql';
import metaobjectQuery from '~graphql/metaobject.gql';
import searchQuery from '~graphql/search.gql';

import { dateTime, debounce, setIntersectionObserver } from '~/helpers/general';

import { formatCustomVariantList } from '~/format/format-custom-variant-list';
import { formatSearch } from '~/format/format-search';
import { formatCollection } from '~/format/format-collection';
import { formatTrackingItem } from '~/format/format-tracking-item';

export default {
  name: 'ProductGrid',

  components: {
    Btn,
    DisturberCard,
    IconLoading,
    IconUpDown,
    ProductCard,
  },

  props: {
    campaignProducts: {
      type: [Boolean, Array],
      default: false,
    },
    campaignLoading: {
      type: [Boolean],
      default: false,
    },
    collectionProductCount: {
      type: Number,
      default: 0,
    },
    defaultSortBy: {
      type: String,
      default: 'best-selling',
    },
    discountPreview: {
      type: Boolean,
      default: false,
    },
    disturber: {
      type: Array,
      default: () => [],
    },
    handle: {
      type: String,
      default: '',
    },
    partial: {
      type: Boolean,
      default: false,
    },
    productsPerPage: {
      type: Number,
      default: 12,
    },
    template: {
      type: String,
      default: 'collection',
    },
    hideSoldout: {
      type: Boolean,
      default: false,
    },
    nextCampaignPage: {
      type: Boolean,
      default: false,
    },
    handleBtnClick: {
      type: Function,
      default: null,
    },
    parentState: {
      type: String,
      default: null,
    },
    infiniteScroll: {
      type: Boolean,
      default: false,
    },
    hideBanner: {
      type: Boolean,
      default: false,
    },
    filteredProducts: {
      type: Array,
      default: () => [],
    },
  },

  emits: {
    state: (payload) => typeof payload === 'string',
    'current-products': (payload) => typeof payload === 'object',
  },

  data() {
    return {
      after: null,
      contentList: [],
      currentSort: {},
      page: {
        hasNextPage: false,
      },
      pageNumber: 1,
      productCount: this.collectionProductCount || 0,
      productCountString: this.$string('accessibility.loading'),
      products: [],
      sortActive: false,
      state: 'initial-load',
      loadButtonIsIntersecting: false,
    };
  },

  computed: {
    ...mapState(useIndexStore, {
      isMobile: (store) => store.screen.mobile,
    }),

    ...mapState(useDiscountCampaignsStore, {
      getCampaignDiscount: 'getCampaignDiscount',
    }),
    ...mapState(usePredictiveSearchStore, ['query']),

    ...mapState(usePredictiveSearchStore, ['query']),

    /**
     * Compute GraphQL query.
     * @returns {String}
     */
    graphqlQuery() {
      if (this.template === 'custom') {
        return metaobjectQuery;
      }

      if (this.template === 'search') {
        return searchQuery;
      }

      return collectionQuery;
    },

    /**
     * Compute graphql query variables.
     * @returns {Object}
     */
    queryVariables() {
      if (this.template === 'custom') {
        return {
          handle: {
            handle: this.handle,
            type: 'custom_collection',
          },
          limit: this.productsPerPage,
        };
      }

      if (this.template === 'search') {
        return {
          query: this.query,
          types: 'PRODUCT',
        };
      }

      return {
        handle: this.handle,
      };
    },

    /**
     * Compute sort options.
     * @returns {Array}
     */
    sortOptions() {
      if (this.template === 'search') {
        return [
          {
            title: this.$string('search.sort_options.relevance'),
            value: {
              id: 'relevance',
              sort: 'RELEVANCE',
              reverse: false,
            },
          },
        ];
      }

      return [
        {
          title: this.$string('collection.sort_options.best_selling'),
          value: {
            id: 'best-selling',
            sort: 'BEST_SELLING',
            reverse: false,
          },
        },
        {
          title: this.$string('collection.sort_options.manual'),
          value: {
            id: 'manual',
            sort: 'MANUAL',
            reverse: false,
          },
        },
        {
          title: this.$string('collection.sort_options.title_ascending'),
          value: {
            id: 'title-ascending',
            sort: 'TITLE',
            reverse: false,
          },
        },
        {
          title: this.$string('collection.sort_options.title_descending'),
          value: {
            id: 'title-descending',
            sort: 'TITLE',
            reverse: true,
          },
        },
      ];
    },

    gridProducts() {
      if (this.filteredProducts.length > 0) {
        return this.filteredProducts;
      }

      return this.contentList;
    },
  },

  watch: {
    filteredProducts: {
      immediate: true,
      handler() {
        this.productCountText(this.page?.hasNextPage);
      },
    },
    /**
     * Watch campaign products to update content list.
     */
    campaignProducts() {
      this.contentList = this.formatContent(this.campaignProducts);
      this.state = 'ready';
      this.trackProductListView();
      this.productCountText(this.page?.hasNextPage);
    },

    /**
     * Watch page number to update URL params and item count.
     */
    pageNumber() {
      if (['campaign', 'custom'].includes(this.template)) {
        return;
      }

      this.updateUrlPageParams(this.pageNumber);
    },

    /**
     * Watch is mobile update to handle resize.
     */
    async isMobile() {
      await this.$nextTick();
      this.updateProductGrid();
    },

    /**
     * Watch and emit state for query text.
     */
    state() {
      if (this.state === 'loading') {
        this.$emit('state', 'loading');
      }

      if (this.state === 'ready') {
        this.$emit('state', 'ready');

        /**
         * trigger load more if infinite scrolling is enabled and button is still
         * in view.
         */
        if (this.infiniteScroll) {
          setTimeout(() => {
            this.handleLoadButtonIntersect();
          }, 500);
        }
      }
    },

    /**
     * Watch partial to update product grid (show partial or full product list).
     */
    partial() {
      this.queryCollection(false, true);
    },
  },

  /**
   * Find current sort from collection's default sort by.
   * - Ensure select displays the correct value.
   */
  beforeMount() {
    const matchingSort = this.sortOptions.find((option) => {
      return option.value.id === this.defaultSortBy;
    });

    if (!matchingSort) {
      return;
    }

    this.currentSort = matchingSort.value;
  },

  mounted() {
    this.getValuesFromParams();
    this.queryCollection();
    this.handleSearchWatcher();
  },

  methods: {
    ...mapActions(useCartStore, {
      getCartAttributeCode: 'getCartAttributeCode',
    }),
    /**
     * Get values from URL params on page load.
     */
    getValuesFromParams() {
      const currentUrl = new URL(window.location);
      const params = new URLSearchParams(currentUrl.search);

      if (params.get('sort_by')) {
        const sort = params.get('sort_by');
        this.sortActive = true;

        this.currentSort =
          this.sortOptions.find((option) => option.value.id === sort)?.value ||
          this.currentSort;
      }

      if (params.get('page')) {
        const page = Number(params.get('page'));
        this.pageNumber = page || 1;
      }

      if (params.get('q')) {
        this.query = params.get('q');
      }
    },

    /**
     * Watch search query and debounce.
     */
    handleSearchWatcher() {
      if (this.template !== 'search') {
        return;
      }

      this.$watch(
        'query',
        debounce((value) => {
          this.after = null;
          this.pageNumber = 1;

          this.queryCollection();

          const currentUrl = new URL(window.location);
          currentUrl.searchParams.set('q', value);
          window.history.replaceState({}, '', currentUrl);
        }, this.$timing('normal')),
      );
    },

    /**
     * Query collection using collection handle.
     * @param {Boolean} keepPrevious - Keep previous results.
     * @returns {Promise}
     */
    queryCollection(keepPrevious = false) {
      return new Promise(async (resolve, reject) => {
        /**
         * If campaign page then do not query products.
         * - Campaign products are queried in campaign-product-list.vue and
         *   passed to this component using the campaignProducts prop.
         */
        if (this.template === 'campaign') {
          this.state = 'loading';
          resolve();
          return;
        }

        this.state = 'loading';

        const first = keepPrevious
          ? this.productsPerPage
          : this.productsPerPage * this.pageNumber;

        let variables = {
          ...this.queryVariables,
          country: cnvs.store.country,
          language: cnvs.store.language,
        };

        variables = {
          ...variables,
          after: this.after,
          first,
          reverse: this.currentSort.reverse,
          [this.template === 'search' ? 'sortKey' : 'sort']:
            this.currentSort.sort,
        };

        try {
          const response = await cnvs.Query({
            query: this.graphqlQuery,
            variables,
          });

          if (this.$variable('settings.disableComponentReadyState')) {
            return;
          }

          if (this.template === 'custom') {
            this.collection = formatCustomVariantList(response);
          }

          if (this.template === 'search') {
            this.collection = formatSearch(response);
            this.productCount = response.search.totalCount;
            cnvs.EventBus.emit('search:results', response.search.totalCount);
          }

          if (this.template === 'collection') {
            this.collection = formatCollection(response);
          }

          this.page.hasNextPage = this.collection.pageInfo?.hasNextPage;
          this.page.cursor = this.collection.pageInfo?.endCursor;

          this.pageNumber = keepPrevious
            ? this.pageNumber + 1
            : this.pageNumber;

          this.after = this.collection.pageInfo?.endCursor || null;

          this.contentList = this.formatContent(
            this.collection.products,
            keepPrevious,
          );

          this.state = 'ready';
          this.$emit('current-products', this.contentList);

          // Track product list view
          this.trackProductListView();

          this.productCountText(this.page.hasNextPage);
          resolve();

          this.trackProductListView();
          this.productCountText(this.page?.hasNextPage);
          resolve();
        } catch (error) {
          cnvs.ReportError('Failed to load products', error);
          reject(error);
        }
      });
    },

    /**
     * Filter product count.
     * - Removes hidden products.
     * @returns {Number}
     */
    productCountText(hasNextPage) {
      let activeProducts = this.contentList.filter((product) => {
        return product.cardType !== 'disturber';
      });

      if (this.filteredProducts.length > 0) {
        activeProducts = this.filteredProducts;
      }

      this.productCount = activeProducts.length;

      if (hasNextPage || this.nextCampaignPage) {
        this.productCountString = this.$string(
          'collection.product_count_more',
          {
            replace: { count: activeProducts.length },
          },
        );

        return;
      }

      this.productCountString = this.$string('collection.product_count', {
        pluralise: this.productCount,
        replace: { count: activeProducts.length },
      });
    },

    /**
     * Format PLP content - products & promos.
     * @param {Object} products - Graphql products data.
     */
    formatContent(products, keepPrevious) {
      this.products = keepPrevious ? this.products.concat(products) : products;

      const productIdsWithFeaturedBanner = new Set();
      const productList = this.products
        .map((product) => {
          const availableState = product.selectedOrFirstAvailableVariant
            ? product.selectedOrFirstAvailableVariant.availableState
            : product.availableState;

          if (
            product.availableState === 'hidden' ||
            (this.hideSoldout && availableState !== 'available')
          ) {
            return false;
          }

          if (
            `${product?.variants[0]?.id}` ===
            this.$variable('product.depositProductID')
          ) {
            return false;
          }

          if (
            product.disableFeaturedBannerMarkets &&
            product.disableFeaturedBannerMarkets.includes(
              this.$variable('store.country'),
            )
          ) {
            delete product.featuredBanner;
          }

          const removeDuplicateFeatureImages = () => {
            if (product.featuredBanner) {
              if (productIdsWithFeaturedBanner.has(product.id)) {
                delete product.featuredBanner;
              }
              productIdsWithFeaturedBanner.add(product.id);
            }
          };
          removeDuplicateFeatureImages();

          if (this.campaignProducts) {
            this.setFirstAvailableVariant(product);
          }

          return {
            ...product,
            cardType: 'product',
          };
        })
        .filter(Boolean);

      if (!productList.length) {
        return [];
      }

      let listWithDisturberCards = false;

      if (!this.sortActive) {
        listWithDisturberCards = this.injectDisturber(productList);
      }

      return listWithDisturberCards || productList;
    },

    /**
     * Updates url param with current page number.
     * @param {Number} page - Number of current page.
     */
    updateUrlPageParams(page) {
      const currentUrl = new URL(window.location);

      if (!page || page <= 1) {
        currentUrl.searchParams.delete('page');
        window.history.replaceState({}, '', currentUrl);
        return;
      }

      currentUrl.searchParams.set('page', page);
      window.history.replaceState({}, '', currentUrl);
    },

    /**
     * Updates url param with current sort value.
     * @param {Object} sort - Object containing current sort values.
     */
    updateUrlSortParams(sort) {
      const currentUrl = new URL(window.location);

      currentUrl.searchParams.delete('page');

      if (!sort) {
        currentUrl.searchParams.delete('sort_by');
        window.history.replaceState({}, '', currentUrl);
        return;
      }

      currentUrl.searchParams.set('sort_by', sort.id);
      window.history.replaceState({}, '', currentUrl);
    },

    /**
     * Inject Disturber Card.
     * @param {Array} list - Product array.
     */
    injectDisturber(list) {
      return this.disturber.forEach((disturber) => {
        const position = this.isMobile
          ? disturber.positionIndex
          : disturber.positionIndexDesktop;

        const startTime = disturber.startTime;
        const endTime = disturber.endTime;

        let result = dateTime('false', 'false');

        if (startTime && endTime) {
          result = dateTime(startTime, endTime);
        }

        if (!result) {
          return;
        }

        list.splice(position, 0, {
          ...disturber,
          cardType: 'disturber',
        });
      });
    },

    /**
     * Reorders product grid with mobile or desktop index position
     * for already valid disturber cards.
     */
    updateProductGrid() {
      const disturberCards = this.contentList.filter(
        (item) => item.cardType === 'disturber',
      );
      const productList = this.contentList.filter(
        (item) => item.cardType !== 'disturber',
      );

      disturberCards.forEach((disturber) => {
        const position = this.isMobile
          ? disturber.positionIndex
          : disturber.positionIndexDesktop;

        productList.splice(position, 0, disturber);
      });

      this.contentList = productList;
    },

    /**
     * Update URL param and fetch product
     */
    handleSort() {
      this.updateUrlSortParams(this.currentSort);
      this.pageNumber = 1;
      this.after = null;
      this.queryCollection();
      this.sortActive = true;
    },

    /**
     * Track product list view
     * - Need to send collection title, id and the product list
     */
    trackProductListView() {
      const products = [...this.contentList]
        .filter((content) => {
          return (
            content.cardType === 'product' &&
            content.availableState !== 'hidden'
          );
        })
        .map((content, index) => {
          const variant = content.selectedOrFirstAvailableVariant;
          const price = variant.price?.amount || 0;
          const compareAtPrice = variant.compareAtPrice?.amount || 0;

          const discountLogic = computeDiscountLogic({
            discountPreview: false,
            onSale: compareAtPrice > price,
            product: content,
            variant,
          });

          const percentageDiscount = this.getCampaignDiscount(
            discountLogic,
            'active',
          );

          const discountObject = {
            percentageDiscount,
            discountCode: this.getCartAttributeCode(),
          };

          const deliveryTime = this.$string('product.delivery_time', {
            replace: { time: this.$variable('product.deliveryTime') },
          });

          const currencyCode = this.$variable('store.currency');

          return formatTrackingItem({
            index,
            product: content,
            variant,
            collection: this.collection,
            template: this.template,
            discountObject,
            deliveryTime,
            currencyCode,
          });
        });

      cnvs.EventBus.emit('dataLayer:view_item_list', {
        collection: this.collection?.handle || this.template || '-',
        collectionId: this.collection?.id || this.handle || '-',
        products,
      });
    },

    setFirstAvailableVariant(product) {
      const isAlreadyAvailable =
        product.selectedOrFirstAvailableVariant.available &&
        product.selectedOrFirstAvailableVariant.availableState === 'available';
      if (isAlreadyAvailable) return;
      if (product?.variants?.length) {
        const nextAvailableVariant = product.variants.find((variant) => {
          return (
            variant.availableState === 'available' && variant.available === true
          );
        });
        if (nextAvailableVariant) {
          product.selectedOrFirstAvailableVariant = nextAvailableVariant;
        }
      }
    },

    loadButtonMounted() {
      // add intersection observer for infinite scrolling
      if (this.infiniteScroll) {
        setIntersectionObserver({
          callback: this.handleLoadButtonIntersect,
          element: this.$refs.loadButton.$el,
          rootMargin: '500px 0px',
          threshold: 0,
        });
      }
    },

    handleLoadButtonIntersect(entries) {
      this.loadButtonIsIntersecting = entries
        ? entries[0].isIntersecting
        : this.loadButtonIsIntersecting;

      if (this.loadButtonIsIntersecting && this.state === 'ready') {
        this.$refs.loadButton.$el.click();
      }
    },
  },
};
</script>

<style lang="scss">
@import './product-grid';
</style>
